import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getPet, getPetWishes, deletePet } from '../services/pets';
import AppLayout from '../components/AppLayout';
import Avatar from '../components/Avatar';
import PetBadge from '../components/PetBadge';
import PetFormModal from '../components/PetFormModal';
import WishFormModal, { BLANK_WISH, wishToForm } from '../components/WishFormModal';
import WishGiftCard from '../components/WishGiftCard';
import { nextBirthday, countdownLabel, revealAfterBirthday } from '../utils/dates';

/**
 * PetDetail — one pet's profile and wish list (/pet/:petId).
 *
 * The pet is shown as the owner of every wish here. Owner-blindness still holds:
 * the caretaker is the wishes' real user, so the API masks claim/purchase state
 * from them and we never render gifting controls in the caretaker's view. Other
 * group members see the pet's wishes as giftable (claim / mark purchased), just
 * like a person's list. Only the caretaker sees the pet's Edit/Delete controls
 * and the "Add a wish" button.
 */

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

export default function PetDetail() {
    const { petId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [pet, setPet] = useState(null);
    const [wishes, setWishes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [myGroups, setMyGroups] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [flash, setFlash] = useState(null); // { type, text }

    const [editingPet, setEditingPet] = useState(false);
    const [wishModal, setWishModal] = useState(null); // { editingId, initialForm }

    // Purchase modal state (gifter view).
    const [purchaseTarget, setPurchaseTarget] = useState(null);
    const [purchaseNote, setPurchaseNote] = useState('');
    const [revealDate, setRevealDate] = useState('');
    const [purchasing, setPurchasing] = useState(false);

    const notify = useCallback((type, text) => {
        setFlash({ type, text });
        if (type === 'success') setTimeout(() => setFlash(null), 3000);
    }, []);

    const reloadWishes = useCallback(async () => {
        // Callers fire this bare after a claim/purchase/delete; own the failure here
        // so a failed refetch can't reject unhandled or silently leave a stale list.
        try {
            setWishes(await getPetWishes(petId));
        } catch {
            notify('error', 'Updated, but the list could not refresh. Pull to reload.');
        }
    }, [petId, notify]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [petData, wishesData, catsRes, groupsRes] = await Promise.all([
                    getPet(petId),
                    getPetWishes(petId),
                    api.get('/wish-categories'),
                    api.get('/groups'),
                ]);
                if (cancelled) return;
                setPet(petData);
                setWishes(wishesData);
                setCategories(catsRes.data);
                setMyGroups(groupsRes.data);
                // Events belong to the pet's group — used by the wish form's
                // optional "attach to event" picker.
                try {
                    const evRes = await api.get(`/groups/${petData.group_id}/events`);
                    if (!cancelled) setEvents(evRes.data);
                } catch {
                    /* events are optional; ignore if unavailable */
                }
            } catch (err) {
                if (cancelled) return;
                // Only a genuine 403/404 on the pet itself means "not found". A
                // transient failure (e.g. /wish-categories blips) must not masquerade
                // as a missing pet — show a retryable error instead.
                const status = err?.response?.status;
                if (status === 403 || status === 404) {
                    setNotFound(true);
                } else {
                    notify('error', errText(err, 'Could not load this pet. Please try again.'));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // notify is a stable useCallback; the effect keys on petId.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petId]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-gray-600">Loading...</div>
            </div>
        );
    }

    if (notFound || !pet) {
        return (
            <AppLayout>
                <div className="text-center py-12 bg-white rounded-lg shadow text-gray-500">
                    This pet could not be found.
                </div>
            </AppLayout>
        );
    }

    const isCaretaker = pet.owner_user_id === user?.id;
    const bday = nextBirthday(pet.birthday);
    const gotcha = nextBirthday(pet.gotcha_day); // reused: next occurrence of any month/day
    // Claim/purchase resolve membership from the pet's group (the viewer can see
    // the pet, so they're a member of it).
    const groupId = pet.group_id;

    // ---- caretaker: pet + wish management ----
    const openAddWish = () => {
        setWishModal({
            editingId: null,
            initialForm: { ...BLANK_WISH, group_ids: [pet.group_id], pet_id: pet.id },
        });
    };

    const openEditWish = async (wish) => {
        try {
            const res = await api.get(`/wishes/${wish.id}`);
            setWishModal({ editingId: wish.id, initialForm: wishToForm(res.data) });
        } catch (err) {
            notify('error', errText(err, 'Could not open that wish for editing.'));
        }
    };

    const handleDeleteWish = async (wish) => {
        if (!window.confirm(`Delete "${wish.title}"? This can't be undone.`)) return;
        try {
            await api.delete(`/wishes/${wish.id}`);
            notify('success', 'Wish deleted.');
            reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not delete the wish.'));
        }
    };

    const handleArchiveWish = async (wish) => {
        try {
            await api.post(`/wishes/${wish.id}/archive`);
            notify('success', 'Wish archived.');
            reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not archive the wish.'));
        }
    };

    const handleDeletePet = async () => {
        if (!window.confirm(
            `Delete "${pet.name}"? All of their wishes go too — including any gifts other members have already claimed or bought. This can't be undone.`
        )) return;
        try {
            await deletePet(pet.id);
            navigate(`/group/${pet.group_id}`);
        } catch (err) {
            notify('error', errText(err, 'Could not delete the pet.'));
        }
    };

    // ---- gifter: claim / purchase ----
    const handleClaim = async (wish) => {
        try {
            await api.post(`/wishes/${wish.id}/claim`, { group_id: groupId });
            notify('success', 'You claimed this — others now see it as reserved.');
            reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not claim the wish.'));
        }
    };

    const handleUnclaim = async (wish) => {
        try {
            await api.delete(`/wishes/${wish.id}/claim`);
            notify('success', 'Claim released.');
            reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not release the claim.'));
        }
    };

    const openPurchase = (wish) => {
        setPurchaseTarget(wish);
        setPurchaseNote('');
        const reveal = revealAfterBirthday(pet.birthday);
        setRevealDate(reveal ? reveal.iso : '');
    };

    const submitPurchase = async (e) => {
        e.preventDefault();
        if (purchasing) return;   // guard against a double-click posting two purchases
        setPurchasing(true);
        try {
            await api.post(`/wishes/${purchaseTarget.id}/purchase`, {
                group_id: groupId,
                private_note: purchaseNote || null,
                reveal_date: revealDate || null,
            });
            notify('success', 'Marked as purchased. The caretaker will not see this.');
            setPurchaseTarget(null);
            reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not record the purchase.'));
        } finally {
            setPurchasing(false);
        }
    };

    const handleUndoPurchase = async (wish) => {
        try {
            await api.delete(`/wishes/${wish.id}/purchase`);
            notify('success', 'Purchase undone.');
            reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not undo the purchase.'));
        }
    };

    return (
        <AppLayout>
            {/* Header: back to the pet's group, pet identity, caretaker actions. */}
            <div className="mb-6">
                <button
                    onClick={() => navigate(`/group/${pet.group_id}`)}
                    className="text-gray-500 hover:text-gray-800 mb-3"
                    aria-label="Back to group"
                >
                    ← Back to group
                </button>
                <div className="flex items-start gap-4">
                    <Avatar name={pet.name} url={pet.avatar_url} color={pet.avatar_color} seed={pet.id} size={64} />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-2xl font-bold text-gray-900">{pet.name}</h1>
                            <PetBadge species={pet.species} />
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                            Cared for by {isCaretaker ? 'you' : pet.owner?.name || 'a group member'}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {bday && (
                                <span className="text-xs px-2 py-0.5 rounded bg-pink-100 text-pink-700">
                                    🎂 {bday.label} · {countdownLabel(bday.daysUntil)}
                                </span>
                            )}
                            {gotcha && (
                                <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
                                    🏡 Gotcha day {gotcha.label} · {countdownLabel(gotcha.daysUntil)}
                                </span>
                            )}
                        </div>
                    </div>
                    {isCaretaker && (
                        <div className="flex items-center gap-3">
                            <button onClick={() => setEditingPet(true)} className="text-sm text-gray-600 hover:text-gray-900">
                                Edit pet
                            </button>
                            <button onClick={handleDeletePet} className="text-sm text-red-600 hover:text-red-700">
                                Delete
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {flash && (
                <div
                    className={`mb-4 p-3 rounded-md text-sm border ${
                        flash.type === 'success'
                            ? 'bg-green-50 border-green-200 text-green-700'
                            : 'bg-red-50 border-red-200 text-red-700'
                    }`}
                >
                    {flash.text}
                </div>
            )}

            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">{pet.name}'s wishes</h2>
                {isCaretaker && (
                    <button onClick={openAddWish} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">
                        + Add a wish
                    </button>
                )}
            </div>

            {wishes.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow text-gray-500">
                    {isCaretaker
                        ? `No wishes for ${pet.name} yet. Add one to get started.`
                        : `${pet.name} has no wishes yet.`}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {wishes.map((wish) => (
                        <WishGiftCard
                            key={wish.id}
                            wish={wish}
                            user={user}
                            ownerLabel="🐾 Your pet's wish"
                            // Owner-blindness (defense in depth): WishGiftCard already
                            // takes the owner branch off the wish's own user_id. We add
                            // the caretaker check on top — the two are equal by an API
                            // invariant today, and OR-ing can only hide gifting UI.
                            forceOwnerView={isCaretaker}
                            ownerActions={
                                <>
                                    <button onClick={() => openEditWish(wish)} className="text-sm text-gray-600 hover:text-gray-900">Edit</button>
                                    <button onClick={() => handleArchiveWish(wish)} className="text-sm text-gray-600 hover:text-gray-900">Archive</button>
                                    <button onClick={() => handleDeleteWish(wish)} className="text-sm text-red-600 hover:text-red-700">Delete</button>
                                </>
                            }
                            onClaim={() => handleClaim(wish)}
                            onUnclaim={() => handleUnclaim(wish)}
                            onPurchase={() => openPurchase(wish)}
                            onUndoPurchase={() => handleUndoPurchase(wish)}
                            notify={notify}
                            onShareChanged={reloadWishes}
                        />
                    ))}
                </div>
            )}

            {/* Edit the pet itself. */}
            {editingPet && (
                <PetFormModal
                    pet={pet}
                    groupId={pet.group_id}
                    onClose={() => setEditingPet(false)}
                    onSaved={(saved) => {
                        setEditingPet(false);
                        setPet((prev) => ({ ...prev, ...saved }));
                        notify('success', 'Pet updated.');
                    }}
                />
            )}

            {/* Add / edit a wish for this pet (pet is locked in). */}
            {wishModal && (
                <WishFormModal
                    editingId={wishModal.editingId}
                    initialForm={wishModal.initialForm}
                    myGroups={myGroups}
                    categories={categories}
                    events={events}
                    lockedGroupId={pet.group_id}
                    pets={[pet]}
                    lockedPetId={pet.id}
                    onClose={() => setWishModal(null)}
                    onSaved={() => {
                        setWishModal(null);
                        reloadWishes();
                    }}
                    notify={notify}
                />
            )}

            {/* Purchase modal (gifter view). */}
            {purchaseTarget && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold mb-1">Mark as purchased</h3>
                        <p className="text-sm text-gray-500 mb-4">
                            “{purchaseTarget.title}”. {pet.name}'s caretaker will never see this.
                        </p>
                        <form onSubmit={submitPurchase} className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Reveal date (optional)</label>
                                <input type="date" value={revealDate} onChange={(e) => setRevealDate(e.target.value)} className={inputCls} />
                                <p className="text-xs text-gray-400 mt-1">
                                    {revealAfterBirthday(pet.birthday)
                                        ? `Defaulted to the day after ${pet.name}'s birthday, so the surprise lasts through it. `
                                        : ''}
                                    On this date the caretaker may be shown who bought it.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Private note (optional)</label>
                                <textarea value={purchaseNote} onChange={(e) => setPurchaseNote(e.target.value)} className={inputCls} rows={2} placeholder="e.g., Ordered, arrives Dec 20" />
                            </div>
                            <div className="flex gap-2 justify-end pt-2">
                                <button type="button" onClick={() => setPurchaseTarget(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">
                                    Cancel
                                </button>
                                <button type="submit" disabled={purchasing} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                    {purchasing ? 'Saving…' : 'Confirm Purchase'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AppLayout>
    );
}

const inputCls =
    'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';
